import {
  type BiddingIntradayCsvRowResult,
  BiddingIntradayCsvRowStatus,
  BiddingIntradayOrderOrderMethod,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import type { IntradayOrderRow } from '~/hooks/bidding/useListBiddingIntradayOrders'
import { civilDateToInputValue } from '~/models/civilDate'
import { fromProtoDate } from '~/models/dateTime'
import {
  type IntradayInversionInput,
  detectIntradayBidInversions,
  isInIntradayInversionPool,
} from '~/models/intradayBidInversion'
import { convertIntradayBidsFromOrders } from '~/pages/IntradayBidding/models/convertIntradayBidsFromOrders'
import { gcBoundarySlot } from '~/pages/IntradayBidding/models/deliveryDate'

/**
 * 売買逆転の判定に渡す、 取り込む行 1 件。 判定に使う形は入札追加タブの札と同じで、
 * 受渡日だけ行ごとに持つ (CSV は 1 ファイルに複数の受渡日が入る)。
 */
export type IntradayCsvInversionCandidate = IntradayInversionInput & {
  targetDate: ProtoDate
}

/**
 * チェック結果の行から、 売買逆転の判定に渡す行を取り出す。 対象は取り込む行
 * (状態が新規) だけ。
 */
export const toIntradayCsvInversionCandidates = (
  rows: readonly BiddingIntradayCsvRowResult[],
): IntradayCsvInversionCandidate[] =>
  rows.flatMap((row) => {
    if (row.status !== BiddingIntradayCsvRowStatus.CREATED) return []
    if (row.targetDate === undefined) return []
    const isMarket = row.orderMethod === BiddingIntradayOrderOrderMethod.MARKET
    // #8800
    if (!isMarket && row.priceYenPerKwh === undefined) return []
    return [
      {
        id: `csv-row-${row.rowNumber}`,
        targetDate: row.targetDate,
        areaCode: row.areaCode,
        timeCode: row.timeCode,
        direction: row.direction,
        // 成行は価格を持たない。 一覧 RPC の札と同じく "0" を入れる (判定は orderMethod を見る)。
        priceYenPerKwh: row.priceYenPerKwh?.value ?? '0',
        orderMethod: row.orderMethod,
      },
    ]
  })

const dateKey = (date: ProtoDate): string => civilDateToInputValue(date)

/** 取り込む行に出てくる受渡日 (初出順・重複なし)。 既存の札はこの受渡日ごとに読む。 */
export const intradayCsvInversionTargetDates = (
  candidates: readonly IntradayCsvInversionCandidate[],
): ProtoDate[] => {
  const byKey = new Map<string, ProtoDate>()
  for (const candidate of candidates) {
    const key = dateKey(candidate.targetDate)
    if (!byKey.has(key)) byKey.set(key, candidate.targetDate)
  }
  return [...byKey.values()]
}

/**
 * 取り込む行が関わる売買逆転 (intraday-bidding.md AC-BIN-045) の件数。
 *
 * 判定とプールの条件は入札追加タブと同じ関数を使う (`detectIntradayBidInversions` /
 * `isInIntradayInversionPool`)。 違うのは 3 点:
 * - 検知単位 (受渡日, エリア, コマ) のうち受渡日を、 行ごとの受渡日で分けてから判定する
 *   (別の受渡日の 買 / 売 を交差扱いしない)
 * - 取り込む行を 1 件も含まない組 (既存の札どうしの交差) は数えない。 この画面で
 *   確かめるのは、 これから取り込む行のこと
 * - 既存の未提出の下書きは入れない。 AC-BIN-045 の対象は「これから提出する札」と
 *   「板にある / 板に載る予定の札」で、 CSV の確定が提出するのは取り込む行だけ
 *   (入札追加タブは未送信の札をまとめて提出するので、 下書きも対象に入れている)
 *
 * @param existingOrders 自社の既存の札 (取り込む行の受渡日ぶん。 他の受渡日が混ざっていてもよい)
 */
// 業務判定を backend に置く原則の例外: 取込を止めない警告 (AC-BCI-035 ※ / AC-BIN-046) のため画面で判定する
export const countIntradayCsvInversions = (
  candidates: readonly IntradayCsvInversionCandidate[],
  existingOrders: readonly IntradayOrderRow[],
  now: Date,
): number => {
  const candidatesByDate = new Map<string, IntradayCsvInversionCandidate[]>()
  for (const candidate of candidates) {
    const key = dateKey(candidate.targetDate)
    const group = candidatesByDate.get(key) ?? []
    group.push(candidate)
    candidatesByDate.set(key, group)
  }

  let count = 0
  for (const [key, dateCandidates] of candidatesByDate) {
    const gcBoundary = gcBoundarySlot(
      now,
      fromProtoDate(dateCandidates[0].targetDate),
    )
    const candidateIds = new Set(dateCandidates.map((c) => c.id))
    const pool: IntradayInversionInput[] = [
      // Step2 を開いている間に GC を過ぎた行は、 確定で対象外になる (spec §4)。
      ...dateCandidates.filter((c) => c.timeCode >= gcBoundary),
      ...convertIntradayBidsFromOrders(
        existingOrders.filter(
          (order) =>
            order.targetDate !== undefined &&
            dateKey(order.targetDate) === key &&
            isInIntradayInversionPool(order, gcBoundary, {
              includeUnsubmittedDrafts: false,
            }),
        ),
      ),
    ]
    count += detectIntradayBidInversions(pool).filter((inversion) =>
      inversion.bidIds.some((id) => candidateIds.has(id)),
    ).length
  }
  return count
}
